import { useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Heading, Modal, Paragraph, Tabs } from '@zillow/constellation-10';

import { useRentalManagerUserPropertyVrModelWebApiHandler_getAllVrModels } from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { ErrorMessageConstants } from '@zg-rentals/zillow-rental-manager/constants';

import { useGetExternalTourProviders } from '../../../hooks/queries/useGetExternalTourProviders';

import ViewTourModal from '../ViewTourModal';
import ExternalTour from './components/ExternalTour';
import ZillowTours from './components/ZillowTours';

import { tabStyles } from './styles';

import SpinnerContainer from '../../SpinnerContainer';
import ModalFooter from '../ModalFooter';

import type {
  GetUserPropertyDetailsExternalTourWebDto,
  GetUserPropertyDetailsVrModelWebDto,
} from '@zg-rentals/ts-rental-manager-api-client';

const SF_NOT_PUBLISHED_TEXT =
  'We won’t able to add this 3d tour to your listing until the listing is live on our ' +
  'sites. Once this listing is live on our sites, please try this operation again.';

type Props = {
  defaultVrModels?: Array<GetUserPropertyDetailsVrModelWebDto>;
  shouldLoadVrModels?: boolean;
  onSubmit?: (
    isZillowTour: boolean,
    selectedVrModelId: string | null | undefined,
    externalTour: GetUserPropertyDetailsExternalTourWebDto,
  ) => Promise<void>;
  onClose: () => void;
  tourType?: 'floorPlan' | 'unit' | 'amenity' | 'singleFamily';
  initialSelectedVrModelId?: string | null;
  initialExternalTour: GetUserPropertyDetailsExternalTourWebDto;
  initialTab?: 'zillow-tour-tab' | 'external-tour-tab';
  zillowDisplayStatus: string;
  filterOutVrModelIds?: Array<string>;
};

const SelectTourModal = ({
  filterOutVrModelIds = [],
  shouldLoadVrModels = true,
  onSubmit = () => Promise.resolve(),
  onClose,
  tourType = 'floorPlan',
  defaultVrModels = [],
  initialSelectedVrModelId = null,
  initialExternalTour,
  initialTab = 'zillow-tour-tab',
  zillowDisplayStatus,
}: Props) => {
  const router = useRouter();
  const externalTourInputRef = useRef<HTMLInputElement>(null);

  const listingAlias = router.query.listingAlias as string;

  const {
    data: vrModelsData,
    isLoading: isLoadingVrModels,
    error: vrModelsError,
  } = useRentalManagerUserPropertyVrModelWebApiHandler_getAllVrModels(
    { listingAlias },
    { enabled: shouldLoadVrModels },
  );

  const vrModels = useMemo(() => {
    if (!shouldLoadVrModels) return defaultVrModels;
    const loadedVrModels = vrModelsData?.vrModels ?? [];
    const filteredVrModels =
      tourType === 'amenity'
        ? loadedVrModels.filter(
            (vm) => vm.vrModelId === initialSelectedVrModelId || filterOutVrModelIds.indexOf(vm.vrModelId || '') === -1,
          )
        : loadedVrModels;
    return filteredVrModels.sort((a, b) => (a.createDateTimeUtc < b.createDateTimeUtc ? 1 : -1));
  }, [vrModelsData, shouldLoadVrModels, defaultVrModels, tourType, filterOutVrModelIds, initialSelectedVrModelId]);

  const [selectedTab, setSelectedTab] = useState(initialTab);
  const [selectedTabToConfirm, setSelectedTabToConfirm] = useState(initialTab);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [tourUrlErrorMessage, setTourUrlErrorMessage] = useState('');
  const [selectedVrModelId, setSelectedVrModelId] = useState<string | null | undefined>(initialSelectedVrModelId);
  const [externalTour, setExternalTour] = useState(initialExternalTour);
  const [showViewTourModal, setShowViewTourModal] = useState(false);
  const [vrModel, setVrModel] = useState<GetUserPropertyDetailsVrModelWebDto | undefined>();

  const isListingPublished = zillowDisplayStatus === 'published' || zillowDisplayStatus === 'posted';
  const isLoading = shouldLoadVrModels && isLoadingVrModels;
  const vrModelsErrorMessage = vrModelsError ? 'Cannot retrieve 3d tours, refresh the page and try again' : undefined;

  /* Prefetching data to avoid a loading spinner on the external tours tab */
  useGetExternalTourProviders();

  const handleSubmit = () => {
    if (selectedTab === 'zillow-tour-tab') {
      if (tourType === 'singleFamily' && !isListingPublished) {
        setErrorMessage(SF_NOT_PUBLISHED_TEXT);
        return;
      }
    }
    setIsSubmitting(true);
    return onSubmit(selectedTab === 'zillow-tour-tab', selectedVrModelId, externalTour)
      .then(() => {
        onClose();
      })
      .catch((errMessage: string) => {
        setIsSubmitting(false);
        if (
          errMessage === ErrorMessageConstants.EXTERNAL_TOUR_INVALID_URL ||
          errMessage === ErrorMessageConstants.EXTERNAL_TOUR_ALREADY_EXIST ||
          errMessage === ErrorMessageConstants.EXTERNAL_TOUR_NOT_SUPPORTED_PROVIDER
        ) {
          setTourUrlErrorMessage(errMessage);
          // ZRM-10774: for error accessibility on screen readers, set focus to the tour url input
          if (externalTourInputRef.current) {
            externalTourInputRef.current.focus();
          }
        } else {
          setErrorMessage(errMessage);
        }
      });
  };

  const handleSelectTour = (id: string) => {
    setErrorMessage(undefined);
    const isUnselecting = selectedVrModelId === id;
    setSelectedVrModelId(isUnselecting ? null : id);
  };

  const handleViewTour = (vrModel: GetUserPropertyDetailsVrModelWebDto) => {
    setVrModel(vrModel);
    setShowViewTourModal(true);
  };

  const handleUpdateExternalTour = (key: string, value: string) => {
    if (key === 'tourUrl' && tourUrlErrorMessage) {
      setTourUrlErrorMessage('');
    }
    if (errorMessage) {
      setErrorMessage('');
    }
    setExternalTour({
      ...externalTour,
      [key]: value,
    });
  };

  const handleConfirmTabChange = () => {
    setSelectedVrModelId(initialSelectedVrModelId);
    setExternalTour(initialExternalTour);
    setTourUrlErrorMessage('');
    setErrorMessage('');
    setShowConfirmationModal(false);
    setSelectedTab(selectedTabToConfirm);
  };

  const handleSelectTab = (selected: string) => {
    if (showConfirmationModal || selected === selectedTab) {
      return;
    }

    const hasPendingChanges =
      selectedVrModelId !== initialSelectedVrModelId ||
      externalTour.tourUrl !== initialExternalTour.tourUrl ||
      externalTour.label !== initialExternalTour.label;
    if (hasPendingChanges) {
      setShowConfirmationModal(true);
      setSelectedTabToConfirm(selected as 'zillow-tour-tab' | 'external-tour-tab');
    } else {
      setSelectedTab(selected as 'zillow-tour-tab' | 'external-tour-tab');
    }
  };

  const vrModelChange = initialSelectedVrModelId !== selectedVrModelId;
  const externalTourChange =
    initialExternalTour.tourUrl !== externalTour.tourUrl || initialExternalTour.label !== externalTour.label;
  const displayErrorMessage = errorMessage || vrModelsErrorMessage;
  const isSubmitDisabled =
    !!displayErrorMessage ||
    !!tourUrlErrorMessage ||
    (!vrModelChange && !externalTourChange) ||
    (selectedTab === 'external-tour-tab' && (!externalTour.tourUrl || !externalTour.label));

  return (
    <Modal
      header={<Heading level={2}>3D tour</Heading>}
      body={
        isLoading ? (
          <SpinnerContainer />
        ) : (
          <Box
            css={{
              marginTop: 'spacing.-400',
              md: {
                minHeight: '600px',
              },
            }}
          >
            <Tabs.Root selected={selectedTab} onSelectedChange={handleSelectTab}>
              <Tabs.List
                aria-label="tour tabs"
                css={{ zIndex: 100000, position: 'sticky', top: 'spacing.-400', backgroundColor: 'bg.neutral' }}
              >
                <Tabs.Tab value="zillow-tour-tab" css={tabStyles}>
                  From Zillow 3D Home app
                </Tabs.Tab>
                <Tabs.Tab value="external-tour-tab" css={tabStyles}>
                  With a link
                </Tabs.Tab>
              </Tabs.List>
              <Tabs.Panel value="zillow-tour-tab">
                <ZillowTours
                  onSelectTour={handleSelectTour}
                  onViewTour={handleViewTour}
                  vrModels={vrModels as Array<GetUserPropertyDetailsVrModelWebDto>}
                  selectedVrModelId={selectedVrModelId}
                  tourType={tourType}
                />
              </Tabs.Panel>
              <Tabs.Panel value="external-tour-tab">
                <ExternalTour
                  externalTour={externalTour}
                  onUpdateExternalTour={handleUpdateExternalTour}
                  tourUrlErrorMessage={tourUrlErrorMessage}
                  externalTourInputRef={externalTourInputRef}
                />
              </Tabs.Panel>
            </Tabs.Root>
            <Modal
              open={showConfirmationModal}
              body={<Paragraph>If you change tabs now, all changes will be lost. Is this what you want?</Paragraph>}
              footer={
                <ModalFooter
                  onCancel={() => setShowConfirmationModal(false)}
                  onSubmit={handleConfirmTabChange}
                  submitText="Yes, discard changes"
                />
              }
            />
            {showViewTourModal && vrModel && (
              <ViewTourModal
                vrModel={{ ...vrModel, name: vrModel.label || '', tourUrl: vrModel.tourUrl || '' }}
                onClose={() => setShowViewTourModal(false)}
              />
            )}
          </Box>
        )
      }
      footer={
        <ModalFooter
          onCancel={onClose}
          onSubmit={handleSubmit}
          submitText="Save"
          isSubmitting={isSubmitting}
          errorMessage={displayErrorMessage}
          hasError={!!displayErrorMessage}
          isSubmitDisabled={isSubmitDisabled}
        />
      }
      onOpenChange={(open) => !open && onClose()}
      open={true}
      size={{ base: 'fullScreen', lg: 'md' }}
    />
  );
};

export default SelectTourModal;
